// services/api/trendService.ts
import { post } from '../../lib/http'
import {
  useQuery,
  useSuspenseQuery,
  type QueryClient,
  keepPreviousData
} from '@tanstack/react-query'
import type {
  VolumeTrendsSummaryRequest,
  VolumeTrendsSummaryResponse,
  VolumeTrendsRangesResponse,
  VolumeTrendsCustomResponse
} from '../../types/DashboardTypes'

export type VolumeTrendsRangesRequest = {
  end_date: string
  verbose?: boolean
  echo_result_columns?: boolean
}

export type VolumeTrendsCustomRequest = {
  start_date: string
  end_date: string
  verbose?: boolean
  echo_result_columns?: boolean
}

/* Fetchers */
export const fetchVolumeTrendsSummary = (
  payload: VolumeTrendsSummaryRequest,
  opts?: { token?: string }
) =>
  post<VolumeTrendsSummaryResponse>(
    '/dashboard/volume-trends-summary',
    payload,
    { bearerToken: opts?.token }
  )

export const fetchVolumeTrendsRanges = (
  payload: VolumeTrendsRangesRequest,
  opts?: { token?: string }
) =>
  post<VolumeTrendsRangesResponse>(
    '/dashboard/volume-trends-summary-ranges',
    payload,
    { bearerToken: opts?.token }
  )

export const fetchVolumeTrendsCustom = (
  payload: VolumeTrendsCustomRequest,
  opts?: { token?: string }
) =>
  post<VolumeTrendsCustomResponse>(
    '/dashboard/volume-trends-summary-custom',
    payload,
    { bearerToken: opts?.token }
  )

/* Keys — PRIMITIVE ONLY (no req objects) */
export const trendKeys = {
  all: ['volumeTrends'] as const,
  summary: (r: VolumeTrendsSummaryRequest) =>
    [
      ...trendKeys.all,
      'summary',
      r.start_date,
      r.end_date,
      !!r.verbose,
      !!r.echo_result_columns
    ] as const,
  ranges: (r: VolumeTrendsRangesRequest) =>
    [
      ...trendKeys.all,
      'ranges',
      r.end_date,
      !!r.verbose,
      !!r.echo_result_columns
    ] as const,
  custom: (r: VolumeTrendsCustomRequest) =>
    [
      ...trendKeys.all,
      'custom',
      r.start_date,
      r.end_date,
      !!r.verbose,
      !!r.echo_result_columns
    ] as const
}

/* Cache policies */
const STALE_SUMMARY_MS = 60_000
const STALE_CUSTOM_MS = 60_000
const STALE_RANGES_MS = 600_000
const GC_RANGES_MS = 900_000

/* Hooks */
export function useVolumeTrendsQuery(
  req: VolumeTrendsSummaryRequest,
  opts?: { token?: string }
) {
  return useQuery({
    queryKey: trendKeys.summary(req),
    queryFn: () => fetchVolumeTrendsSummary(req, { token: opts?.token }),
    staleTime: STALE_SUMMARY_MS,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  })
}
export function useVolumeTrendsSuspenseQuery(
  req: VolumeTrendsSummaryRequest,
  opts?: { token?: string }
) {
  return useSuspenseQuery({
    queryKey: trendKeys.summary(req),
    queryFn: () => fetchVolumeTrendsSummary(req, { token: opts?.token }),
    staleTime: STALE_SUMMARY_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  })
}
export function useVolumeTrendsRangesQuery(
  req: VolumeTrendsRangesRequest,
  opts?: { token?: string }
) {
  return useQuery({
    queryKey: trendKeys.ranges(req),
    queryFn: () => fetchVolumeTrendsRanges(req, { token: opts?.token }),
    staleTime: STALE_RANGES_MS,
    gcTime: GC_RANGES_MS,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  })
}
export function useVolumeTrendsRangesSuspenseQuery(
  req: VolumeTrendsRangesRequest,
  opts?: { token?: string }
) {
  return useSuspenseQuery({
    queryKey: trendKeys.ranges(req),
    queryFn: () => fetchVolumeTrendsRanges(req, { token: opts?.token }),
    staleTime: STALE_RANGES_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  })
}
export function useVolumeTrendsCustomQuery(
  req: VolumeTrendsCustomRequest,
  opts?: { token?: string }
) {
  return useQuery({
    queryKey: trendKeys.custom(req),
    queryFn: () => fetchVolumeTrendsCustom(req, { token: opts?.token }),
    staleTime: STALE_CUSTOM_MS,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  })
}
export function useVolumeTrendsCustomSuspenseQuery(
  req: VolumeTrendsCustomRequest,
  opts?: { token?: string }
) {
  return useSuspenseQuery({
    queryKey: trendKeys.custom(req),
    queryFn: () => fetchVolumeTrendsCustom(req, { token: opts?.token }),
    staleTime: STALE_CUSTOM_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  })
}

/* Prefetch / invalidate unchanged except keys now primitive-only */
export const prefetchVolumeTrends = (
  qc: QueryClient,
  req: VolumeTrendsSummaryRequest,
  token?: string
) =>
  qc.prefetchQuery({
    queryKey: trendKeys.summary(req),
    queryFn: () => fetchVolumeTrendsSummary(req, { token }),
    staleTime: STALE_SUMMARY_MS
  })
export const prefetchVolumeTrendsRanges = (
  qc: QueryClient,
  req: VolumeTrendsRangesRequest,
  token?: string
) =>
  qc.prefetchQuery({
    queryKey: trendKeys.ranges(req),
    queryFn: () => fetchVolumeTrendsRanges(req, { token }),
    staleTime: STALE_RANGES_MS
  })
export const prefetchVolumeTrendsCustom = (
  qc: QueryClient,
  req: VolumeTrendsCustomRequest,
  token?: string
) =>
  qc.prefetchQuery({
    queryKey: trendKeys.custom(req),
    queryFn: () => fetchVolumeTrendsCustom(req, { token }),
    staleTime: STALE_CUSTOM_MS
  })
export const invalidateVolumeTrends = (qc: QueryClient) =>
  qc.invalidateQueries({ queryKey: trendKeys.all })
export const invalidateVolumeTrendsSummary = (
  qc: QueryClient,
  req?: VolumeTrendsSummaryRequest
) =>
  qc.invalidateQueries({
    queryKey: req ? trendKeys.summary(req) : [...trendKeys.all, 'summary']
  })
export const invalidateVolumeTrendsRanges = (
  qc: QueryClient,
  req?: VolumeTrendsRangesRequest
) =>
  qc.invalidateQueries({
    queryKey: req ? trendKeys.ranges(req) : [...trendKeys.all, 'ranges']
  })
export const invalidateVolumeTrendsCustom = (
  qc: QueryClient,
  req?: VolumeTrendsCustomRequest
) =>
  qc.invalidateQueries({
    queryKey: req ? trendKeys.custom(req) : [...trendKeys.all, 'custom']
  })
